'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface Notification {
  id: string;
  userId: string;
  shipmentId: string;
  notificationType: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];

    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

function convertNotificationFromDb(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    shipmentId: row.shipment_id,
    notificationType: row.notification_type,
    title: row.title,
    message: row.message,
    read: row.read,
    createdAt: row.created_at,
  };
}

interface RealtimeNotificationsProps {
  userId: string | null;
  maxDisplay?: number;
}

export default function RealtimeNotifications({ userId, maxDisplay = 5 }: RealtimeNotificationsProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    // Initial fetch
    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            setNotifications([]);
          }
        } else {
          setNotifications(data?.map(convertNotificationFromDb) || []);
        }
      } catch (err: any) {
        console.log('Schema-related error:', err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Real-time subscription
    const channel = supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setNotifications((current) => [convertNotificationFromDb(payload.new), ...current]);
          } else if (payload.eventType === 'UPDATE') {
            setNotifications((current) =>
              current?.map((notif) =>
                notif?.id === payload?.new?.id ? convertNotificationFromDb(payload.new) : notif
              ) || []
            );
          } else if (payload.eventType === 'DELETE') {
            setNotifications((current) => current?.filter((notif) => notif?.id !== payload?.old?.id) || []);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', userId!);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
        }
      }
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId!)
        .eq('read', false);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
        }
      }
    } catch (err: any) {
      console.log('Schema-related error:', err.message);
      throw err;
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'delivery_confirmation':
        return 'CheckCircleIcon';
      case 'delivery_milestone':
        return 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png';
      case 'customs_alert':
        return 'ExclamationTriangleIcon';
      case 'delay_alert':
        return 'ClockIcon';
      default:
        return 'BellIcon';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'delivery_confirmation':
        return 'text-success';
      case 'delivery_milestone':
        return 'text-primary';
      case 'customs_alert':
        return 'text-warning';
      case 'delay_alert':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications?.filter((n) => !n.read)?.length || 0;
  const displayedNotifications = showAll ? notifications : notifications?.slice(0, maxDisplay) || [];

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BellIcon" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-muted rounded-lg p-4 h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="BellIcon" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-error text-error-foreground text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {notifications?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="BellIcon" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No notifications yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedNotifications?.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 rounded-lg border transition-all cursor-pointer ${
                  notification.read
                    ? 'bg-muted/30 border-border' :'bg-primary/5 border-primary/20 hover:bg-primary/10'
                }`}
                onClick={() => !notification.read && markAsRead(notification.id)}
              >
                <div className="flex items-start gap-3">
                  <Icon
                    name={getNotificationIcon(notification.notificationType)}
                    size={20}
                    className={`shrink-0 mt-0.5 ${getNotificationColor(notification.notificationType)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-semibold text-foreground text-sm">{notification.title}</h4>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-primary rounded-full shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{notification.message}</p>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(notification.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {notifications?.length > maxDisplay && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {showAll ? 'Show less' : `Show all (${notifications.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}